import classNames from "classnames";
import { Field, Form, withFormik } from "formik";
import Button from "pages/_components/Button";
import Credential from "pages/_components/fields/credentials/Credential";
import Head from "pages/_components/Head";
import I18n from "pages/_components/I18n";
import Notification from "pages/_components/Notification";
import { bool, func, shape, string } from "prop-types";
import React, { Component } from "react";
import Col from "react-bootstrap/lib/Col";
import Row from "react-bootstrap/lib/Row";
import { connect } from "react-redux";
import { goBack, push } from "react-router-redux";
import { actions as transferActions, selectors as transferSelectors } from "reducers/transfer";
import { compose } from "redux";
import { getAccountDescription } from "util/accounts";
import * as i18nUtils from "util/i18n";
import Yup from "yup";

import Container from "pages/_components/Container";
import Hint from "pages/_components/hints/Hint";
import MainContainer from "pages/_components/MainContainer";
import { resizableRoute } from "pages/_components/Resizable";
import TransferSummaryData from "pages/transfer/TransferSummaryData";
import getTransferPath from "util/transfers";
import { applyEmbeddedV3ShellLayoutState, clearEmbeddedV3ShellLayoutState } from "util/v3ShellLayoutSync";
import { selectors as sessionSelectors } from "reducers/session";
import * as configUtils from "util/config";
import isTokenActive from "util/token";
import { getTransferUxI18nKey } from "util/transferInternalTitleLabels";

const FORM_ID = "transferSummary";
class TransferSummary extends Component {
    static propTypes = {
        dispatch: func.isRequired,
        isSubmitting: bool.isRequired,
        summary: shape({
            debitAccount: string.isRequired,
            creditAccount: string.isRequired,
            amount: string.isRequired,
            currency: string.isRequired,
            reference: string,
            email: string,
            emailText: string,
            tadTrx: bool,
            idActivity: string,
        }).isRequired,
        internalTransfer: bool.isRequired,
        // eslint-disable-next-line react/no-unused-prop-types
        transferGroup: string.isRequired,
        isDesktop: bool.isRequired,
        embeddedInV3Shell: bool,
    };

    static defaultProps = {
        embeddedInV3Shell: false,
    };

    componentDidMount() {
        const {
            summary: { debitAccount },
            dispatch,
            internalTransfer,
        } = this.props;
        if (!debitAccount) {
            dispatch(push(getTransferPath(internalTransfer ? "internal" : "thirdParties")));
        }
        this.syncV3ShellLayout();
    }

    componentDidUpdate(prevProps) {
        const { embeddedInV3Shell, isDesktop, internalTransfer, transferGroup, summary } = this.props;
        if (
            prevProps.embeddedInV3Shell !== embeddedInV3Shell ||
            prevProps.isDesktop !== isDesktop ||
            prevProps.internalTransfer !== internalTransfer ||
            prevProps.transferGroup !== transferGroup ||
            prevProps.summary?.idActivity !== summary?.idActivity
        ) {
            this.syncV3ShellLayout();
        }
    }

    componentWillUnmount() {
        const { embeddedInV3Shell, dispatch } = this.props;
        if (embeddedInV3Shell) {
            clearEmbeddedV3ShellLayoutState(dispatch);
        }
    }

    getAccountDescription({ number, productType, currency }) {
        const { dispatch, internalTransfer } = this.props;
        if (!number) {
            dispatch(push(getTransferPath(internalTransfer ? "internal" : "thirdParties")));
            return "";
        }
        return getAccountDescription({ number, productType, currency });
    }

    getShellGoBackPath = () => {
        const { internalTransfer, transferGroup } = this.props;
        if (transferGroup === "toContact") {
            return getTransferPath("toContact");
        }
        return getTransferPath(internalTransfer ? "internal" : "thirdParties");
    };

    getSummaryTitleKey = () => {
        const { summary, internalTransfer, transferGroup } = this.props;
        const idActivity = summary?.idActivity;

        if (idActivity === "transfers.internal.send" || idActivity === "transfers.internal.tad.send") {
            return getTransferUxI18nKey("transfers.summary.internal.label");
        }
        if (idActivity === "transfers.thirdParties.send" || idActivity === "transfers.thirdParties.tad.send") {
            return "transfers.summary.external.label";
        }
        if (idActivity === "transfers.toContact.send" || idActivity === "transfers.toContact.tad.send") {
            return "transfers.summary.toContact.label";
        }
        if (transferGroup === "toContact") {
            return "transfers.summary.toContact.label";
        }
        return internalTransfer
            ? getTransferUxI18nKey("transfers.summary.internal.label")
            : "transfers.summary.external.label";
    };

    syncV3ShellLayout = () => {
        const { dispatch, embeddedInV3Shell, isDesktop } = this.props;
        if (!embeddedInV3Shell) {
            return;
        }
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["form"],
            titleKey: this.getSummaryTitleKey(),
            goBackPath: this.getShellGoBackPath(),
        });
    };

    handleBack = () => {
        const { dispatch } = this.props;
        dispatch(goBack());
    };

    render() {
        const { embeddedInV3Shell, isDesktop, isSubmitting, summary } = this.props;

        const continueButton = (
            <Col className="col-12 confirmation__content-button" lg={12} md={12} sm={12}>
                <Row>
                    <div className="admin-content-center">
                        <Button
                            image="images/arrowRight.svg"
                            imageStyle={isDesktop ? "mr-2" : "mr-1"}
                            type="submit"
                            bsStyle="primary"
                            label="global.confirm"
                            loading={isSubmitting}
                        />
                    </div>
                </Row>
            </Col>
        );

        const credentials = (
            <>
                <Row className="my-2">
                    {!isTokenActive(!isDesktop) ? (
                        <div className="admin-content-center">
                            <I18n
                                id="transfer.summary.confirm.label"
                                componentProps={{
                                    className: classNames("font-light", {
                                        "f-size-65": !isDesktop,
                                        "f-size-55": isDesktop,
                                    }),
                                }}
                            />
                        </div>
                    ) : (
                        <div className="admin-content-center pb-3">
                            <I18n
                                id="confirmation.withoutotp.text"
                                component="p"
                                componentProps={{ className: "confirmation__text-without-otp" }}
                            />
                        </div>
                    )}
                </Row>
                <Row>
                    <div className="admin-content-center">
                        <Field
                            name="otp"
                            type="otp"
                            idForm={FORM_ID}
                            component={Credential}
                            placeholderText={i18nUtils.get(`confirmation.otp.placeholder`)}
                        />
                    </div>
                </Row>
            </>
        );
        return (
            <>
                {!embeddedInV3Shell && <Notification scopeToShow="form" />}
                {(!embeddedInV3Shell || isDesktop) && (
                    <div className="admin-detail-head px-0">
                        <Head
                            onBack={this.handleBack}
                            headerClassName={!isDesktop ? "blue-main-header-mobile" : ""}
                            title={!isDesktop && this.getSummaryTitleKey()}
                            centerContentClassName={classNames({
                                "mx-5": !isDesktop,
                            })}
                        />
                        {isDesktop && (
                            <div className="d-flex">
                                <div className="mx-3">
                                    <h1>{i18nUtils.get(this.getSummaryTitleKey())}</h1>
                                </div>
                                {summary?.control && (
                                    <div className="ms-5">
                                        <Hint
                                            defaultValue={configUtils.get("frontend.mobileSignatureControl.text", "")}
                                            classNameGroup="d-flex align-items-center w-100 mb-2"
                                            classNameHintP="m-0 ms-2"
                                            alwaysShowMessage
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {embeddedInV3Shell && !isDesktop && summary?.control && (
                    <div className="mb-3 px-5">
                        <Hint
                            defaultValue={configUtils.get("frontend.mobileSignatureControl.text", "")}
                            classNameGroup="d-flex align-items-center w-100 mb-2"
                            classNameHintP="m-0 ms-2"
                            alwaysShowMessage
                        />
                    </div>
                )}
                <MainContainer>
                    <Form>
                        <Container
                            className={classNames("flex-grow align-items-center confirmation__container", {
                                "mb-0 cmf-container-white": !isDesktop,
                            })}
                            gridClassName="form-content">
                            {!isDesktop && (
                                <div
                                    className={classNames("admin-content-center", {
                                        "mb-3": !isDesktop,
                                    })}>
                                    <I18n
                                        id="transfers.summary.checkInfo.label"
                                        component="h2"
                                        componentProps={{ className: classNames({ "f-size-35": !isDesktop }) }}
                                    />
                                </div>
                            )}

                            <Col className="col-12 confirmation__form" lg={6} md={9} sm={12}>
                                {isDesktop && (
                                    <div
                                        className={classNames("admin-content-center", {
                                            "mb-3": !isDesktop,
                                        })}>
                                        <I18n
                                            id="transfers.summary.checkInfo.label"
                                            component={!isDesktop ? "h2" : "h1"}
                                            componentProps={{ className: classNames({ "f-size-35": !isDesktop }) }}
                                        />
                                    </div>
                                )}
                                <TransferSummaryData isDesktop={isDesktop} />
                                {credentials}
                                <Container className="flex-grow align-items-center w-100" gridClassName="form-content">
                                    {continueButton}
                                </Container>
                            </Col>
                        </Container>
                    </Form>
                </MainContainer>
            </>
        );
    }
}
const mapStateToProps = (state) => ({
    summary: transferSelectors.getSummary(state),
    internalTransfer: transferSelectors.getInternalTransfer(state),
    transferGroup: transferSelectors.getTransferGroup(state),
    userId: sessionSelectors.getUser(state).userId,
    envName: sessionSelectors.getActiveEnvironment(state).name,
    envDocument: sessionSelectors.getActiveEnvironment(state).clients[0]?.idClient,
});

export default compose(
    connect(mapStateToProps),
    withFormik({
        mapPropsToValues: () => ({ otp: "" }),
        validationSchema: () =>
            Yup.object().shape({
                otp: Yup.string().required(i18nUtils.get(`${FORM_ID}.otp.error.required`)),
            }),
        handleSubmit: ({ otp }, formikBag) => {
            const { dispatch, summary, internalTransfer, userId, transferGroup } = formikBag.props;
            const addTadActivity = summary?.tadTrx ? ".tad" : "";

            if (transferGroup === "toContact") {
                dispatch(
                    transferActions.sendTransferToContact(
                        { ...summary, idActivity: `transfers.toContact${addTadActivity}.send`, userId, formikBag },
                        otp,
                    ),
                );
            } else if (internalTransfer) {
                dispatch(
                    transferActions.sendInternalTransfer(
                        { ...summary, idActivity: `transfers.internal${addTadActivity}.send`, userId, formikBag },
                        otp,
                    ),
                );
            } else {
                dispatch(
                    transferActions.sendExternalTransfer(
                        { ...summary, idActivity: `transfers.thirdParties${addTadActivity}.send`, userId, formikBag },
                        otp,
                    ),
                );
            }
        },
    }),
    resizableRoute,
)(TransferSummary);
